// Curly braces denote a resource token; both `{` and `}` must be present.
export const RESOURCE_TOKEN_REGEX = /\{([^{}\n]+)\}/g;

/** Inner text of each complete `{inner}` token (trimmed). Incomplete tokens like `{foo` are ignored. */
export function extractResourceTokenInners(text: string): string[] {
	const out: string[] = [];
	const re = new RegExp(RESOURCE_TOKEN_REGEX.source, "g");
	let m: RegExpExecArray | null;
	while ((m = re.exec(text)) !== null) {
		out.push(m[1].trim());
	}
	return out;
}

export const CONSTANT_TOKEN_REGEX = /"([^"\n]*)"/g;

export const OPERATOR_TOKEN_REGEX = /(?:>=|<=|<>|[+\-*/=<>])/g;

const FUNCTION_NAME_OPEN_PAREN_REGEX = /[A-Z][A-Z0-9_]*\(/g;

/**
 * Collects half-open `[start, end)` spans for `{resource}` tokens and
 * `"constant"` strings. These regions are opaque when matching function
 * parentheses.
 */
function findOpaqueRanges(text: string): Array<[number, number]> {
	const ranges: Array<[number, number]> = [];
	let cursor = 0;
	while (cursor < text.length) {
		const character = text[cursor];
		if (character === "{") {
			const closingBraceIndex = text.indexOf("}", cursor + 1);
			if (closingBraceIndex === -1) {
				cursor++;
				continue;
			}
			ranges.push([cursor, closingBraceIndex + 1]);
			cursor = closingBraceIndex + 1;
			continue;
		}
		if (character === '"') {
			const closingQuoteIndex = text.indexOf('"', cursor + 1);
			if (closingQuoteIndex === -1) {
				cursor++;
				continue;
			}
			ranges.push([cursor, closingQuoteIndex + 1]);
			cursor = closingQuoteIndex + 1;
			continue;
		}
		cursor++;
	}
	return ranges;
}

/** True when `index` falls inside any half-open `[start, end)` span. */
function isIndexInsideRanges(index: number, ranges: Array<[number, number]>): boolean {
	return ranges.some(([start, end]) => index >= start && index < end);
}

/**
 * Finds the open `{` for an incomplete resource token immediately before the
 * cursor. Skips `{` inside closed `{…}` tokens and `"…"` strings.
 */
export function findActiveResourceQuery(
	textBeforeCursor: string,
): { openIndex: number; query: string } | null {
	if (!textBeforeCursor.length) {
		return null;
	}

	const opaqueRanges = findOpaqueRanges(textBeforeCursor);

	for (let index = textBeforeCursor.length - 1; index >= 0; index--) {
		if (textBeforeCursor[index] !== "{") {
			continue;
		}
		if (isIndexInsideRanges(index, opaqueRanges)) {
			continue;
		}
		const query = textBeforeCursor.slice(index + 1);
		if (query.includes("}")) {
			continue;
		}
		return { openIndex: index, query };
	}

	return null;
}

/**
 * Returns the index of the closing `)` for the `(` at `openParenIndex`.
 * Brace and quote regions are skipped entirely, so parentheses inside
 * `{resources}` or `"constants"` do not affect depth counting.
 */
function findMatchingCloseParen(text: string, openParenIndex: number): number | null {
	let parenDepth = 1;
	let cursor = openParenIndex + 1;
	while (cursor < text.length) {
		const character = text[cursor];
		if (character === "{") {
			const closingBraceIndex = text.indexOf("}", cursor + 1);
			if (closingBraceIndex === -1) {
				cursor++;
				continue;
			}
			cursor = closingBraceIndex + 1;
			continue;
		}
		if (character === '"') {
			const closingQuoteIndex = text.indexOf('"', cursor + 1);
			if (closingQuoteIndex === -1) {
				cursor++;
				continue;
			}
			cursor = closingQuoteIndex + 1;
			continue;
		}
		if (character === "(") {
			parenDepth++;
			cursor++;
			continue;
		}
		if (character === ")") {
			parenDepth--;
			if (parenDepth === 0) return cursor;
			cursor++;
			continue;
		}
		cursor++;
	}
	return null;
}

/**
 * Finds every `FUNCTION_NAME(...)` call in `text` as half-open `[start, end)`
 * spans suitable for syntax highlighting.
 *
 * Nested calls (e.g. `SUM(ROUND({Amount}))`) each produce their own span.
 * When a closing `)` is missing, the span runs to the end of the string.
 */
export function findFunctionCallRanges(
	text: string
): Array<{ start: number; end: number }> {
	const opaqueRanges = findOpaqueRanges(text);
	const namePattern = new RegExp(FUNCTION_NAME_OPEN_PAREN_REGEX.source, "g");
	const functionRanges: Array<{ start: number; end: number }> = [];
	let match: RegExpExecArray | null;
	while ((match = namePattern.exec(text)) !== null) {
		const functionStart = match.index;
		if (isIndexInsideRanges(functionStart, opaqueRanges)) continue;

		const openParenIndex = functionStart + match[0].length - 1;
		const closeParenIndex = findMatchingCloseParen(text, openParenIndex);
		functionRanges.push({
			start: functionStart,
			end: closeParenIndex !== null ? closeParenIndex + 1 : text.length,
		});
	}
	return functionRanges;
}
